// eslint-disable-next-line import/no-cycle
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';

import { Heading } from '@/components/common/Heading';

type Word = {
  _key: string;
  _type: 'combination';
  colour: string;
  word: string;
};
type HeadingTextSliderProps = {
  _key: string;
  children: string[];
  words: Word[];
  firstWordColour: string;
  isBold: boolean;
  fontStyle: string;
};

const HeadingWithUnderline = ({ name, accentColour, fontStyle, isBold }) => {
  const headingWithUnderlineMock = [
    {
      _key: 'block',
      _type: 'block' as const,
      children: [
        {
          _key: 'text',
          _type: 'span',
          marks: ['thin-underline', isBold ? 'strong' : ''],
          text: name,
        },
      ],
      markDefs: [
        {
          _key: 'thin-underline',
          _type: 'highlight',
          colour: accentColour,
          style: 'thin-underline',
        },
      ],
      style: fontStyle,
    },
  ];
  return <Heading heading={headingWithUnderlineMock} elementType="span" />;
};

const Animation = ({
  _key,
  children,
  words,
  firstWordColour,
  fontStyle,
  isBold,
}: HeadingTextSliderProps) => {
  const wordsCombined: Word[] = [
    {
      _key,
      _type: 'combination',
      colour: firstWordColour,
      word: children[0],
    },
    ...words,
  ];

  const fadeTranisitionTime = 3000;
  const maxCycles = 3;
  const totalItems = wordsCombined.length;

  const [activeSlide, setActiveSlide] = useState(1);
  const cycle = useRef(1);

  const cycleWords = useCallback(() => {
    const timeout = setTimeout(() => {
      if (cycle.current <= maxCycles) {
        if (totalItems === activeSlide) {
          cycle.current += 1;
          setActiveSlide(1);
        } else {
          setActiveSlide(activeSlide + 1);
        }
      }
    }, fadeTranisitionTime);

    // end of animation - remove setTimeout
    if (cycle.current > maxCycles) {
      clearTimeout(timeout);
    }
  }, [activeSlide, totalItems]);

  useEffect(() => {
    if (children) cycleWords();
  });

  const getClassNames = (index: number) => {
    return classNames({
      initial: activeSlide === 1 && cycle.current === 1,
      active: index === activeSlide,
      // previous slide is either:
      // when index (plus one) is the same as active slide - 1 (since index starts from 0)
      // or when the first slide is active, and the index (plus one) equals the total amount of items
      previous:
        index === activeSlide - 1 ||
        (activeSlide === 1 && index === wordsCombined.length),
    });
  };

  return (
    <HeadingItems>
      {wordsCombined.map((data, index) => {
        return (
          <HeadingContainer
            key={data._key}
            className={getClassNames(index + 1)}
          >
            <HeadingWithUnderline
              name={data.word}
              accentColour={data.colour}
              fontStyle={fontStyle}
              isBold={isBold}
            />
          </HeadingContainer>
        );
      })}
    </HeadingItems>
  );
};

export const HeadingItems = styled.div`
  position: relative;
`;

export const HeadingContainer = styled.div`
  @keyframes grow {
    from {
      width: 0%;
    }

    to {
      width: 100%;
    }
  }
  opacity: 0;
  position: absolute;
  bottom: 20px;

  span svg {
    width: 0%;
  }

  &&.previous {
    opacity: 0;
    bottom: -10px;
    transition-duration: 0.6s;
    transition-property: opacity, bottom;
    transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);

    svg {
      opacity: 0;
    }
  }

  &&.active {
    position: relative;
    opacity: 1;
    bottom: 0px;
    transition-delay: 0.4s;
    transition-property: opacity, bottom;
    transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
    transition-duration: 0.6s;

    svg {
      animation-timing-function: cubic-bezier(0.65, 1, 0.35, 1);
      animation-delay: 0.4s;
      animation-duration: 0.4s;
      animation-name: grow !important;
      animation-fill-mode: forwards;
    }
  }

  &&.initial {
    svg {
      animation-delay: 0.8s;
    }
  }

  @media (prefers-reduced-motion) {
    &&.previous {
      transition-duration: 0s;
    }

    &&.active {
      transition-delay: 0s;
      transition-duration: 0s;

      svg {
        transition-delay: 0s;
        transition-duration: 0s;
      }
    }
  }
`;

export { Animation };
